import { useRestApiProvider } from "@jugl-web/rest-api";
import { getFileFromURL } from "@jugl-web/utils/utils/files";
import { useCallback, useMemo } from "react";
import { CHAT_BOT_DEFAULT_SETTINGS } from "../consts";
import { ChatBotSettings } from "../types";
import { getDefaultCTAVisual } from "../utils/defaultCTAVisuals";

const SUPPORTED_SETTINGS_KEYS = new Set(Object.keys(CHAT_BOT_DEFAULT_SETTINGS));

const FILE_STORAGE_BASE_URL =
  "https://jugl-chatbot-data.s3.eu-west-3.amazonaws.com";

type UpdateChatBotSettingsPayload = Partial<
  Omit<
    ChatBotSettings,
    "chatImageUrl" | "agentImage" | "buttonIcon" | "ctaVisual"
  > & {
    chatImage: File | string;
    agentImage: File | string;
    buttonIcon: File | string;
    ctaVisual: File | string | null;
  }
>;

interface UseUpdateChatBotSettingsOptions {
  entityId: string;
}

type ChatBotImageFileType = "launcher_icon" | "avatar_icon" | "chat_icon";

type FileToUpload =
  | { type: ChatBotImageFileType; file: File }
  | { type: "welcome_msg_media"; id: string; file: File };

const chatBotImageFileTypeToSettingsKey: Record<
  ChatBotImageFileType,
  keyof ChatBotSettings
> = {
  launcher_icon: "buttonIcon",
  avatar_icon: "agentImage",
  chat_icon: "chatImageUrl",
};

export const useUpdateChatBotSettings = ({
  entityId,
}: UseUpdateChatBotSettingsOptions) => {
  const { integrationsApi } = useRestApiProvider();

  const { data } = integrationsApi.useGetWebChatBotConfigQuery({ entityId });

  const [updateWebChatBotConfig, { isLoading: isChatBotSettingsSaving }] =
    integrationsApi.useUpdateWebChatBotConfigMutation();

  const [uploadChatBotFiles] = integrationsApi.useUploadChatBotFilesMutation();

  const defaultSettings = useMemo<ChatBotSettings>(
    () => structuredClone(CHAT_BOT_DEFAULT_SETTINGS),
    []
  );

  const currentSettings = useMemo<ChatBotSettings>(() => {
    if (!data?.settings) {
      return defaultSettings;
    }

    const rawSettings = structuredClone(data.settings);

    // Clean up settings keys that are not supported anymore
    Object.keys(rawSettings).forEach((key) => {
      if (!SUPPORTED_SETTINGS_KEYS.has(key)) {
        delete rawSettings[key];
      }
    });

    return { ...defaultSettings, ...rawSettings };
  }, [data?.settings, defaultSettings]);

  const updateChatBotSettings = useCallback(
    async (payload: UpdateChatBotSettingsPayload) => {
      const { chatImage, agentImage, buttonIcon, ctaVisual, ...rest } = payload;

      const updatedSettings: ChatBotSettings = {
        ...currentSettings,
        ...rest,
      };

      const filesToUpload: FileToUpload[] = [];

      if (typeof chatImage !== "undefined") {
        if (chatImage instanceof File) {
          filesToUpload.push({
            type: "chat_icon",
            file: chatImage,
          });
        } else {
          updatedSettings.chatImageUrl = chatImage;
        }
      }

      if (typeof agentImage !== "undefined") {
        if (agentImage instanceof File) {
          filesToUpload.push({
            type: "avatar_icon",
            file: agentImage,
          });
        } else {
          updatedSettings.agentImage = agentImage;
        }
      }

      if (typeof buttonIcon !== "undefined") {
        if (buttonIcon instanceof File) {
          filesToUpload.push({
            type: "launcher_icon",
            file: buttonIcon,
          });
        } else {
          updatedSettings.buttonIcon = buttonIcon;
        }
      }

      if (typeof ctaVisual !== "undefined") {
        // custom visual is explicitly disabled
        if (ctaVisual === null) {
          updatedSettings.ctaVisual = null;
          // default ID or custom visual passed as URL
        } else if (typeof ctaVisual === "string") {
          if (
            ctaVisual.startsWith("default") &&
            currentSettings.ctaVisual?.id !== ctaVisual
          ) {
            const defaultVisual = getDefaultCTAVisual(ctaVisual);

            const file = await getFileFromURL(
              defaultVisual.src,
              `${defaultVisual.id}.webm`,
              "video/webm"
            );

            filesToUpload.push({
              type: "welcome_msg_media",
              id: defaultVisual.id,
              file,
            });
          }
          // custom visual passed as file
        } else if (ctaVisual instanceof File) {
          filesToUpload.push({
            type: "welcome_msg_media",
            id: "custom",
            file: ctaVisual,
          });
        }
      }

      if (filesToUpload.length > 0) {
        const response = await uploadChatBotFiles({
          entityId,
          files: filesToUpload,
        });

        if (!("data" in response)) {
          throw new Error("Uploading chat bot files failed");
        }

        filesToUpload.forEach((entry) => {
          const fileName = entry.type;
          const fileExtension = entry.file.name.split(".").pop();
          const hash = crypto.randomUUID().substring(0, 5);
          const fileUrl = `${FILE_STORAGE_BASE_URL}/${entityId}/chatbot_files/${fileName}.${fileExtension}?v=${hash}`;

          if (entry.type === "welcome_msg_media") {
            updatedSettings.ctaVisual = { id: entry.id, url: fileUrl };
          } else {
            (updatedSettings[
              chatBotImageFileTypeToSettingsKey[entry.type]
            ] as string) = fileUrl;
          }
        });
      }

      await updateWebChatBotConfig({
        entityId,
        settings: {
          ...currentSettings,
          ...rest,
          ...updatedSettings,
        },
      }).unwrap();
    },
    [currentSettings, entityId, updateWebChatBotConfig, uploadChatBotFiles]
  );

  return { updateChatBotSettings, isChatBotSettingsSaving };
};
